import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const jobTypes = ["composite", "mechanical"] as const;

// Placeholder image paths - update to your actual assets (e.g., in public/images/)
const jobTypeImages: Record<(typeof jobTypes)[number], string> = {
  composite: "src/assets/images/composite.png",
  mechanical: "src/assets/images/mechanical.png",
};

type SelectJobType = {
  open: boolean;
  setOpen: (open: boolean) => void;
  allowedJobTypes: ("mechanical" | "composite")[];
};

export default function SelectJobType({ open, setOpen, allowedJobTypes }: SelectJobType) {
  const filteredJobTypes = jobTypes.filter((type) => allowedJobTypes.includes(type));
  const [selectedType, setSelectedType] = useState<(typeof jobTypes)[number]>(filteredJobTypes[0] || jobTypes[0]);
  const navigate = useNavigate();

  // Reset selectedType if it's not in the filtered list
  useEffect(() => {
    if (filteredJobTypes.length > 0 && !filteredJobTypes.includes(selectedType)) {
      setSelectedType(filteredJobTypes[0]);
    }
  }, [filteredJobTypes, selectedType]);

  const handleGoToCreate = () => {
    if (selectedType === "composite") {
      navigate({
        pathname: "/jobs/create/composite",
      });
    } else if (selectedType === "mechanical") {
      navigate({
        pathname: "/jobs/create/mechanical",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="flex flex-col items-center">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center">Select Job Type</DialogTitle>
          <DialogDescription className="text-center">
            Choose between composite or mechanical job type to continue
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 w-full max-w-md">
          {filteredJobTypes.map((item) => (
            <div
              key={item}
              role="radio"
              aria-checked={selectedType === item}
              tabIndex={0}
              onClick={() => setSelectedType(item)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedType(item);
                }
              }}
              className={`flex flex-row items-center space-x-4 p-3 rounded-lg cursor-pointer transition-colors ${
                selectedType === item
                  ? "border-2 border-blue-500 bg-blue-50"
                  : "border border-gray-200 hover:bg-gray-100"
              }`}
            >
              <img
                src={jobTypeImages[item]}
                alt={`${item.charAt(0).toUpperCase() + item.slice(1)} job type`}
                className="h-20 w-20 object-contain flex-shrink-0"
              />
              <span className="text-md font-medium capitalize">{item}</span>
            </div>
          ))}
        </div>
        <Button onClick={handleGoToCreate} className="mt-4 w-full max-w-md">
          Continue
        </Button>
      </DialogContent>
    </Dialog>
  );
}
